import React from 'react';
import { Label, Loader, Message, Segment, SemanticCOLORS, Table } from 'semantic-ui-react';

import Button from '../../../components/Button';
import { maskCpfOrCnpj, maskMoney } from '../../../services/masks';
import { StandaloneChargeDTO, StandaloneChargeStatusEnum } from '../../../services/standalone-charges';
import TablePaginationMenu from '../../../components/TablePaginationMenu';

import { ListActionType } from '../types';

const statusLabelColor: Record<string, string> = {
  [StandaloneChargeStatusEnum.pendingPayment]: 'yellow',
  [StandaloneChargeStatusEnum.paid]: 'green',
  [StandaloneChargeStatusEnum.cancelled]: 'red',
  [StandaloneChargeStatusEnum.expired]: 'grey'
};

type Props = {
  isLoading: boolean;
  items: StandaloneChargeDTO[];
  statusLabelMap: Record<string, string>;
  currentPage: number;
  total: number;
  setCurrentPage: (page: number) => void;
  actionLoading: { chargeId: string; action: ListActionType } | null;
  userCanSend: boolean;
  userCanCancel: boolean;
  userCanRenew: boolean;
  /** Quando false (ex.: usuário de imobiliária), oculta a coluna Imobiliária. */
  showEstateColumn: boolean;
  onRowClick: (chargeId: string) => void;
  onCopyLink: (link: string, chargeId: string) => void;
  onResendEmail: (id: string) => void;
  onCancel: (charge: StandaloneChargeDTO) => void;
  onRenew: (id: string) => void;
};

const stopRowOpen = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

const ChargesTable: React.FC<Props> = ({
  isLoading,
  items,
  statusLabelMap,
  currentPage,
  total,
  setCurrentPage,
  actionLoading,
  userCanSend,
  userCanCancel,
  userCanRenew,
  showEstateColumn,
  onRowClick,
  onCopyLink,
  onResendEmail,
  onCancel,
  onRenew
}) => {
  const columnCount = showEstateColumn ? 9 : 8;

  if (isLoading) {
    return (
      <>
        <Segment basic style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader active inline="centered">
            Carregando cobranças…
          </Loader>
        </Segment>
        <TablePaginationMenu currentPage={currentPage} dataCount={total} setCurrentPage={setCurrentPage} />
      </>
    );
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <Table
          celled
          striped
          unstackable
          selectable
          style={{ minWidth: showEstateColumn ? 980 : 840 }}
          aria-label="Tabela de cobranças"
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Data</Table.HeaderCell>
              <Table.HeaderCell>Expiração</Table.HeaderCell>
              {showEstateColumn && <Table.HeaderCell>Imobiliária</Table.HeaderCell>}
              <Table.HeaderCell>Pagador</Table.HeaderCell>
              <Table.HeaderCell>Método</Table.HeaderCell>
              <Table.HeaderCell>Documento</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Valor</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columnCount}>
                  <Message info style={{ margin: 0 }}>
                    <Message.Header>Nenhuma cobrança encontrada</Message.Header>
                    <p style={{ margin: '0.5em 0 0' }}>
                      Ajuste os filtros ou crie uma nova cobrança para ela aparecer aqui.
                    </p>
                  </Message>
                </Table.Cell>
              </Table.Row>
            ) : (
              items.map(item => {
                const isCopyLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'copy';
                const isResendLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'resend';
                const isCancelLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'cancel';
                const isRenewLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'renew';
                const statusColor = statusLabelColor[item.status] || 'grey';

                return (
                  <Table.Row
                    key={item.id}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => onRowClick(item.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(item.id);
                      }
                    }}
                    aria-label={`Abrir detalhes da cobrança de ${item.payerName}`}
                  >
                    <Table.Cell>
                      {item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : '-'}
                    </Table.Cell>
                    <Table.Cell>
                      {item.expiresAt ? new Date(item.expiresAt).toLocaleString('pt-BR') : '-'}
                    </Table.Cell>
                    {showEstateColumn && <Table.Cell>{item.estate?.name || '-'}</Table.Cell>}
                    <Table.Cell>{item.payerName}</Table.Cell>
                    <Table.Cell>{item.paymentMethod || '-'}</Table.Cell>
                    <Table.Cell>{maskCpfOrCnpj(item.payerCpf)}</Table.Cell>
                    <Table.Cell textAlign="right">
                      {`R$ ${maskMoney(String(Number(item.amount).toFixed(2)), true)}`}
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={statusColor as SemanticCOLORS} size="small">
                        {statusLabelMap[item.status] || item.status}
                      </Label>
                    </Table.Cell>
                    <Table.Cell onClick={stopRowOpen}>
                      <div
                        style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
                        role="group"
                        aria-label="Ações da cobrança"
                      >
                        {item.status === StandaloneChargeStatusEnum.pendingPayment &&
                          <Button Button
                            size="mini"
                            basic
                            loading={isCopyLoading}
                            disabled={Boolean(actionLoading)}
                            onClick={e => {
                              e.stopPropagation();
                              onCopyLink(item.chargeLinkUrl, item.id);
                            }}
                          >
                            Copiar link
                          </Button>}
                        {item.status === StandaloneChargeStatusEnum.pendingPayment && userCanSend && (
                          <Button
                            size="mini"
                            basic
                            color="blue"
                            loading={isResendLoading}
                            disabled={Boolean(actionLoading)}
                            onClick={e => {
                              e.stopPropagation();
                              onResendEmail(item.id);
                            }}
                          >
                            Reenviar e-mail
                          </Button>
                        )}
                        {item.status === StandaloneChargeStatusEnum.pendingPayment && userCanCancel && (
                          <Button
                            size="mini"
                            basic
                            color="red"
                            loading={isCancelLoading}
                            disabled={Boolean(actionLoading)}
                            onClick={e => {
                              e.stopPropagation();
                              onCancel(item);
                            }}
                          >
                            Cancelar
                          </Button>
                        )}
                        {item.status === StandaloneChargeStatusEnum.expired && userCanRenew && (
                          <Button
                            size="mini"
                            basic
                            color="blue"
                            loading={isRenewLoading}
                            disabled={Boolean(actionLoading)}
                            onClick={e => {
                              e.stopPropagation();
                              onRenew(item.id);
                            }}
                          >
                            Renovar Cobrança
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </div >
      <TablePaginationMenu currentPage={currentPage} dataCount={total} setCurrentPage={setCurrentPage} />
    </>
  );
};

export default ChargesTable;
